"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { SectionHeading } from "@/components/ui/editorial/SectionHeading";
import { Caption } from "@/components/ui/editorial/Caption";
import { achievements } from "../data/achievements";

const AboutMeSection = () => {
	return (
		<section className="p-4 xl:px-0 py-16" aria-label="Sobre mí">
			<div className="container max-w-7xl mx-auto space-y-12">
				{/* Header */}
				<motion.header
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
				>
					<SectionHeading
						index="01"
						title={
							<>
								Sobre <em className="italic text-terracotta">mí</em>
							</>
						}
						as="h2"
					/>
				</motion.header>

				{/* Main Content */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
					{/* Text Content */}
					<motion.div
						initial={{ opacity: 0, x: -30 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
						className="space-y-6"
					>
						<div className="space-y-5">
							<p className="font-body text-ink text-lg leading-relaxed">
								Soy <strong>Aranzazu Gutierrez Butler</strong>, escritora,
								conferenciante y activista social comprometida con la
								transformación de realidades a través de la literatura y el
								activismo.
							</p>

							<p className="font-body text-ink text-lg leading-relaxed">
								Mi novela <strong>&quot;Todo lo que no ves&quot;</strong> nació
								de una profunda reflexión sobre las heridas invisibles de la
								infancia y la importancia de crear vínculos familiares
								saludables. Esta obra se ha convertido en una herramienta de
								sensibilización y transformación social.
							</p>

							<p className="font-body text-ink text-lg leading-relaxed">
								Como fundadora de la <strong>Asociación Equilibrio</strong>,
								trabajo incansablemente para crear espacios de reflexión,
								educación emocional y prevención de la violencia de género,
								especialmente dirigidos a jóvenes y familias.
							</p>
						</div>

						{/* Quote */}
						<motion.blockquote
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							viewport={{ once: true }}
							className="border-l-2 border-terracotta pl-6 py-2"
						>
							<p className="font-body text-ink text-lg italic leading-relaxed mb-3">
								&quot;La transformación social comienza cuando nos atrevemos a
								ver lo que otros prefieren ocultar, y cuando decidimos actuar
								para cambiar esas realidades.&quot;
							</p>
							<cite className="font-display text-sm text-terracotta not-italic">
								— Aranzazu Gutierrez Butler
							</cite>
						</motion.blockquote>
					</motion.div>

					{/* Image */}
					<motion.div
						initial={{ opacity: 0, x: 30 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
					>
						<figure>
							<div className="rounded-2xl overflow-hidden border border-border">
								<Image
									src="/assets/todo-lo-que-no-ves/a-1-results.jpg"
									alt="Aranzazu Gutierrez Butler trabajando en su escritorio"
									width={500}
									height={600}
									className="object-cover w-full h-auto"
								/>
							</div>
							<Caption>Aranzazu Gutierrez Butler</Caption>
						</figure>
					</motion.div>
				</div>

				{/* Achievements Grid */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px border border-border rounded-xl overflow-hidden bg-border"
				>
					{achievements.map((achievement, index) => {
						const IconComponent = achievement.icon;
						return (
							<motion.div
								key={index}
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: index * 0.08 }}
								viewport={{ once: true }}
								className="bg-cream p-8 space-y-3"
							>
								<div className="w-8 h-8 text-terracotta">
									<IconComponent className="w-8 h-8" />
								</div>
								<h3 className="font-display font-semibold text-ink text-base">
									{achievement.title}
								</h3>
								<p className="font-body text-muted-foreground text-sm leading-relaxed">
									{achievement.description}
								</p>
							</motion.div>
						);
					})}
				</motion.div>
			</div>
		</section>
	);
};

export default AboutMeSection;
